import { Box, Text, useColorMode } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { BLINKING_CURSOR_VISIBLE_TIME, DISPLAY_TEXT_TIME_SPEED } from 'helpers/constant';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useRef, useState } from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import { useSelector } from 'react-redux';

const TypingText = () => {
  const [displayText, setDisplayText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const { colorMode } = useColorMode();
  const cursorColorMode = colorMode === 'light' ? 'black' : 'white';

  const blinkCursorRef = useRef<HTMLDivElement>(null);

  const controls = useAnimation();
  const { current, processingStreamingChunkStatus } = useSelector((store) => store.conversations);
  const setRefBoxHeight = current.answerChunks.chunk ? '70px' : '';

  useEffect(() => {
    blinkCursorRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [current.answerChunks.chunk, processingStreamingChunkStatus, blinkCursorRef]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayText(current.answerChunks.chunk.substring(0, displayText.length + 1));
    }, DISPLAY_TEXT_TIME_SPEED);

    return () => clearInterval(interval);
  }, [displayText, current.answerChunks.chunk]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, BLINKING_CURSOR_VISIBLE_TIME);

    return () => clearInterval(cursorInterval);
  }, []);

  const cleanLatex = (latexString: string): string => {
    return latexString
      .replace(/\\\[\s*\n/g, '\\[')
      .replace(/\n\s*\\\]/g, ' \\]')
      .trim();
  };

  const formatAnswer = (answer: string) => {
    const lines = answer.split('\n');
    let isTable = false;
    let isCodeBlock = false;
    let tableLines: string[] = [];
    let codeBlockLines: string[] = [];
    const bulletListRegex = /^\s*([*+-])\s+/;
    const blockQuoteRegex = /^\s*>/;

    const renderedLines = lines.map((line, lineIndex) => {
      switch (true) {
        case line.trim().startsWith('|') && line.includes('|'):
          isTable = true;
          tableLines.push(line);

          if (lineIndex === lines.length - 1 || !lines[lineIndex + 1].includes('|')) {
            isTable = false;
            const tableContent = tableLines.join('\n');
            tableLines = [];
            return (
              <div key={lineIndex} className='table-wrapper'>
                <Markdown className='table-content' options={{ enforceAtxHeadings: true }}>
                  {tableContent}
                </Markdown>
              </div>
            );
          }
          return null;

        case line.trim().startsWith('```'):
          if (!isCodeBlock) {
            isCodeBlock = true;
          } else {
            isCodeBlock = false;
            const codeBlockContent = codeBlockLines.join('\n');
            codeBlockLines = [];

            const innerRenderedContent = formatAnswer(codeBlockContent);
            return (
              <div key={lineIndex} className='codeblock'>
                <pre>
                  <code>{innerRenderedContent}</code>
                </pre>
              </div>
            );
          }
          return null;

        case isTable:
          tableLines.push(line);
          return null;

        case isCodeBlock:
          codeBlockLines.push(line);
          return null;

        case blockQuoteRegex.test(line.trim()):
          const blockQuoteContent = line.replace(blockQuoteRegex, '').trim();
          return (
            <blockquote key={lineIndex} className='custom-blockquote'>
              {blockQuoteContent}
            </blockquote>
          );

        default:
          if (bulletListRegex.test(line.trim())) {
            const indentation = line.match(/^\s*/)?.[0]?.length || 0;
            const indentationLevel = Math.floor(indentation / 2);

            const bulletMatch = line.trim().match(bulletListRegex);

            if (bulletMatch) {
              const bulletSymbol = bulletMatch[0];

              return (
                <div
                  key={`bullet-${lineIndex}`}
                  style={{ paddingLeft: `${indentationLevel * 20}px` }}
                >
                  <span>{bulletSymbol}</span>
                  {renderMixedContent(line.trim().slice(bulletMatch[0].length))}
                </div>
              );
            }
          }

          return <div key={lineIndex}>{renderMixedContent(line)}</div>;
      }
    });

    return renderedLines;
  };

  const renderMixedContent = (line: string) => {
    const inlineMathPattern = /\\\((.*?)\\\)/g;
    const blockMathPattern = /\\\[(.*?)\\\]/;
    const markdownOptions = {
      enforceAtxHeadings: true, // Only parse headers when # is followed by space
    };

    const elements: JSX.Element[] = [];
    let remaining = line;
    let blockMatch;

    if ((blockMatch = blockMathPattern.exec(remaining)) !== null) {
      const [fullBlockMatch, blockFormula] = blockMatch;

      const precedingText = remaining.slice(0, blockMatch.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <div key={elements.length} style={{ display: 'block', margin: '0' }}>
          <BlockMath>{blockFormula}</BlockMath>
        </div>
      );

      remaining = remaining.slice(blockMatch.index + fullBlockMatch.length);
    }

    let lastIndex = 0;
    let match;
    while ((match = inlineMathPattern.exec(remaining)) !== null) {
      const [fullMatch, formula] = match;

      const precedingText = remaining.slice(lastIndex, match.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <span
          key={elements.length}
          style={{ display: 'inline', marginLeft: '10px', marginRight: '5px' }}
        >
          <InlineMath>{formula.trim()}</InlineMath>
        </span>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < remaining.length) {
      const remainingText = remaining.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{remainingText.trim()}</Markdown>
          </span>
        );
      }
    }

    return <>{elements}</>;
  };
  const cleandisplayText = cleanLatex(displayText ?? '');

  return (
    <>
      <div style={{ display: 'inline-block', position: 'relative' }}>
        <motion.span
          style={{ marginRight: '4px' }}
          animate={controls}
          initial={{ opacity: 1 }}
          className='markdown-container'
        >
          {formatAnswer(cleandisplayText)}
        </motion.span>
        {cursorVisible && (
          <Text
            display={'inline-block'}
            w={'8px'}
            h={'15px'}
            bgColor={cursorColorMode}
            content=''
          ></Text>
        )}
      </div>
      <Box ref={blinkCursorRef} h={setRefBoxHeight}></Box>
    </>
  );
};

export default TypingText;
